import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { PageViewTracker } from "./components/analytics/PageViewTracker";

// Eager: the two highest-traffic entry points paint without a second fetch.
import Index from "./pages/Index";
import Jobs from "./pages/Jobs";

// Everything else loads on demand — admin analytics alone is a large chunk
// (recharts et al) that no professional's phone should ever download.
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const HospitalSettings = lazy(() => import("./pages/HospitalSettings"));
const MinimalProfileDetails = lazy(() => import("./pages/MinimalProfileDetails"));
const ProfessionalProfile = lazy(() => import("./pages/ProfessionalProfile"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const NearbyJobs = lazy(() => import("./pages/NearbyJobs"));
const HospitalProfile = lazy(() => import("./pages/HospitalProfile"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogList = lazy(() => import("./pages/admin/BlogList"));
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"));
const AnalyticsShell = lazy(() =>
  import("./pages/admin/analytics/AnalyticsShell").then((m) => ({ default: m.AnalyticsShell }))
);
const NotFound = lazy(() => import("./pages/NotFound"));
const ForHospitals = lazy(() => import("./pages/ForHospitals"));
const ForProfessionals = lazy(() => import("./pages/ForProfessionals"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const VerificationProcess = lazy(() => import("./pages/VerificationProcess"));
const JobsLanding = lazy(() => import("./pages/JobsLanding"));
const NavigationReview = lazy(() => import("./pages/dev/NavigationReview"));
const LogoComparison = lazy(() => import("./pages/dev/LogoComparison"));
const PostShiftEntry = lazy(() => import("./pages/PostShiftEntry"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

const queryClient = new QueryClient();

function FacilityRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/hospitals/${slug}`} replace />;
}

/** Minimal, brand-neutral loading state for lazy chunks. */
function RouteFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary/20 border-t-primary" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageViewTracker />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/settings" element={<HospitalSettings />} />
            <Route path="/select-role" element={<Navigate to="/complete-profile" replace />} />
            <Route path="/complete-profile" element={<MinimalProfileDetails />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/landing/:segment" element={<JobsLanding />} />
            <Route path="/jobs/:slug" element={<Jobs />} />
            <Route path="/profile" element={<ProfessionalProfile />} />
            <Route path="/profile/:id" element={<ProfessionalProfile />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/analytics/actions" replace />} />
            <Route path="/admin/metrics" element={<Navigate to="/admin/analytics/actions" replace />} />
            <Route path="/admin/manage" element={<Admin />} />
            <Route path="/admin/analytics/*" element={<AnalyticsShell />} />
            <Route path="/nearby" element={<NearbyJobs />} />
            <Route path="/hospitals/:slug" element={<HospitalProfile />} />
            <Route path="/facility/:slug" element={<FacilityRedirect />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin/blog" element={<BlogList />} />
            <Route path="/admin/blog/edit/:id" element={<BlogEditor />} />
            <Route path="/for-hospitals" element={<ForHospitals />} />
            <Route path="/for-professionals" element={<ForProfessionals />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/verification" element={<Navigate to="/verification-process" replace />} />
            <Route path="/verification-process" element={<VerificationProcess />} />
            <Route path="/post-a-shift" element={<PostShiftEntry />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            {import.meta.env.DEV ? (
              <>
                <Route path="/dev/navigation-review" element={<NavigationReview />} />
                <Route path="/dev/logo-comparison" element={<LogoComparison />} />
              </>
            ) : null}
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
